import React, { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import PantryRow from "../components/PantryRow";
import BottomNav from "../components/BottomNav";
import PwaIcon from "../components/PwaIcon";
import DateField from "../components/DateField";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerFooter,
} from "../components/ui/drawer";
import { Loader2, Minus, Plus, Check, Trash2, Pencil, X, ChevronDown, ChevronRight, Settings2, Search } from "lucide-react";
import CategoryManager from "../components/CategoryManager";
import PantrySwitcher from "../components/PantrySwitcher";
import InstallButton from "../components/InstallButton";
import { useT } from "../i18n";

const groupKeys = ["red", "yellow", "green", "expired"];

const plusDaysIso = (d) => new Date(Date.now() + d * 86400000).toISOString().slice(0, 10);

export default function Dashboard() {
  const { user } = useAuth();
  const { t, formatDate } = useT();
  const groupTints = {
    red: "text-fresh-red-text",
    yellow: "text-fresh-yellow-text",
    green: "text-fresh-green-text",
    expired: "text-zinc-600",
  };
  const groups = groupKeys.map((k) => ({ key: k, label: t(`dashboard.group.${k}`), tint: groupTints[k] }));
  const location = useLocation();
  const navigate = useNavigate();
  const [data, setData] = useState({ grouped: { red: [], yellow: [], green: [], expired: [], needs_review: [], scanning: [], pending: [] }, count: 0, scanning_count: 0, pending_count: 0, needs_review_count: 0 });
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editItem, setEditItem] = useState(null);
  const [selectedCat, setSelectedCat] = useState("all"); // "all" | "none" | category_id
  const [selectedFresh, setSelectedFresh] = useState("all"); // "all" | "red" | "yellow" | "green" | "expired"
  const [expandedParents, setExpandedParents] = useState({});
  const [catManagerOpen, setCatManagerOpen] = useState(false);
  const [search, setSearch] = useState("");

  // Ref guard: while the edit drawer is open, stop any background refresh so
  // the user's typing / fields never get clobbered by a re-render.
  const editOpenRef = useRef(false);
  useEffect(() => { editOpenRef.current = !!editItem; }, [editItem]);

  const load = async () => {
    if (editOpenRef.current) return; // never refresh while editing
    try {
      const r = await api.get("/items");
      setData(r.data);
    } catch (_e) {
      toast.error(t("dashboard.toast.cant_load"));
    } finally { setLoading(false); }
  };

  const loadCategories = async () => {
    try {
      const r = await api.get("/categories");
      setCategories(r.data.categories || []);
    } catch (_e) { /* noop */ }
  };

  useEffect(() => { load(); loadCategories(); }, []);

  // Refresh when window regains focus (skipped while drawer open)
  useEffect(() => {
    const onFocus = () => { load(); };
    const onVisibility = () => { if (document.visibilityState === "visible") load(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  const scanningCount = data.scanning_count || (data.grouped?.scanning?.length || 0);
  useEffect(() => {
    if (!scanningCount) return undefined;
    const h = setInterval(() => { load(); }, 2000);
    return () => clearInterval(h);
  }, [scanningCount]);

  const onConsume = async (item) => {
    try {
      await api.post(`/items/${item.item_id}/consume`);
      toast.success(t("dashboard.toast.consumed", { name: item.product_name }));
      load();
    } catch (_e) { toast.error(t("dashboard.toast.error")); }
  };
  const onDecrement = async (item) => {
    try {
      const r = await api.post(`/items/${item.item_id}/consume?qty=1`);
      if (r.data?.consumed) toast.success(t("dashboard.toast.consumed", { name: item.product_name }));
      else toast.success(t("dashboard.toast.remaining", { n: r.data?.remaining ?? "—" }));
      load();
    } catch (_e) { toast.error(t("dashboard.toast.error")); }
  };
  const onIncrement = async (item) => {
    try {
      await api.patch(`/items/${item.item_id}`, { quantity: (item.quantity || 1) + 1 });
      load();
    } catch (_e) { toast.error(t("dashboard.toast.error")); }
  };
  const onDelete = async (item) => {
    try {
      await api.delete(`/items/${item.item_id}`);
      toast.success(t("dashboard.toast.deleted"));
      load();
    } catch (_e) { toast.error(t("dashboard.toast.error")); }
  };

  const scrollToGroup = (key) => {
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="group-${key}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 150);
  };

  const groupFromItem = (it) => {
    if (it.days_left == null) return "expired";
    if (it.days_left < 0) return "expired";
    if (it.days_left < 5) return "red";
    if (it.days_left <= 15) return "yellow";
    return "green";
  };

  const labelForGroup = (g) => t(`dashboard.group.${g}`);

  const confirmOne = async (item) => {
    if (typeof item.days_left === "number" && item.days_left < 0) {
      toast.warning(t("dashboard.toast.stale_warn", { date: formatDate(item.expiry_date) }));
      setEditItem({ ...item, _pending: true });
      return;
    }
    try {
      await api.post(`/items/${item.item_id}/confirm`);
      const g = groupFromItem(item);
      toast.success(t("dashboard.toast.inserted", { name: item.product_name, group: labelForGroup(g) }));
      await load();
      scrollToGroup(g);
    } catch (_e) { toast.error(t("dashboard.toast.create_failed")); }
  };

  const confirmAll = async () => {
    const stale = (totals.pending || []).filter((i) => typeof i.days_left === "number" && i.days_left < 0);
    if (stale.length > 0) {
      toast.warning(t("dashboard.toast.stale_n", { n: stale.length }));
      scrollToGroup("pending");
      return;
    }
    try {
      const r = await api.post(`/items/confirm-all`);
      toast.success(t("dashboard.toast.confirmed_n", { n: r.data.confirmed || 0 }));
      await load();
      for (const k of ["red", "yellow", "green", "expired"]) {
        if ((totals[k] || []).length > 0 || (data.grouped[k] || []).length > 0) {
          scrollToGroup(k);
          break;
        }
      }
    } catch (_e) { toast.error(t("dashboard.toast.create_failed")); }
  };

  const openManualCreate = () => {
    setEditItem({
      _new: true,
      product_name: "",
      expiry_date: plusDaysIso(7),
      quantity: 1,
      category_id: selectedCat !== "all" && selectedCat !== "none" ? selectedCat : null,
    });
  };

  // Listen for FAB "Manuale" event (dispatched by BottomNav)
  useEffect(() => {
    const handler = () => openManualCreate();
    window.addEventListener("pantry:manual-add", handler);
    return () => window.removeEventListener("pantry:manual-add", handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCat]);

  // Auto-open manual drawer when arriving via /dashboard?manual=1
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("manual") === "1") {
      openManualCreate();
      navigate("/dashboard", { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const createManual = async () => {
    if (!editItem?.product_name?.trim() || !editItem?.expiry_date) {
      toast.error(t("dashboard.toast.required"));
      return;
    }
    try {
      await api.post(`/items`, {
        product_name: editItem.product_name.trim(),
        expiry_date: editItem.expiry_date,
        quantity: Math.max(1, Number(editItem.quantity) || 1),
        category_id: editItem.category_id || null,
      });
      const g = groupFromItem({ ...editItem, days_left: Math.ceil((new Date(editItem.expiry_date) - new Date()) / 86400000) });
      toast.success(t("dashboard.toast.created_in", { group: labelForGroup(g) }));
      closeEdit();
      await load();
      scrollToGroup(g);
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      const m = /^plan_limit:max_items:(\d+)$/.exec(detail);
      if (m) toast.error(t("plan.limit.items", { n: m[1] }));
      else toast.error(t("dashboard.toast.create_failed"));
    }
  };

  const saveEdit = async () => {
    if (!editItem) return;
    if (!editItem.product_name?.trim() || !editItem.expiry_date) {
      toast.error(t("dashboard.toast.required"));
      return;
    }
    try {
      await api.patch(`/items/${editItem.item_id}`, {
        product_name: editItem.product_name.trim(),
        expiry_date: editItem.expiry_date,
        quantity: Math.max(1, Number(editItem.quantity) || 1),
        category_id: editItem.category_id === undefined ? null : (editItem.category_id || ""),
      });
      toast.success(t("dashboard.toast.saved"));
      closeEdit();
      load();
    } catch (_e) {
      toast.error(t("dashboard.toast.save_failed"));
    }
  };

  const saveAndConfirm = async () => {
    if (!editItem) return;
    if (!editItem.product_name?.trim() || !editItem.expiry_date) {
      toast.error(t("dashboard.toast.required"));
      return;
    }
    try {
      await api.patch(`/items/${editItem.item_id}`, {
        product_name: editItem.product_name.trim(),
        expiry_date: editItem.expiry_date,
        quantity: Math.max(1, Number(editItem.quantity) || 1),
        category_id: editItem.category_id === undefined ? null : (editItem.category_id || ""),
      });
      await api.post(`/items/${editItem.item_id}/confirm`);
      toast.success(t("dashboard.toast.saved"));
      closeEdit();
      load();
    } catch (_e) {
      toast.error(t("dashboard.toast.save_failed"));
    }
  };

  const totals = data.grouped || { red: [], yellow: [], green: [], expired: [], needs_review: [], scanning: [], pending: [] };
  const reviewItems = totals.needs_review || [];
  const scanningItems = totals.scanning || [];
  const pendingItems = totals.pending || [];

  // Combined filter (freshness + category)
  // If a parent category is selected, items in any of its children also match.
  const childIdsByParent = useMemo(() => {
    const map = {};
    for (const c of categories) {
      if (c.parent_id) {
        map[c.parent_id] = map[c.parent_id] || [];
        map[c.parent_id].push(c.category_id);
      }
    }
    return map;
  }, [categories]);

  const catFilter = (it) => {
    if (selectedCat === "all") return true;
    if (selectedCat === "none") return !it.category_id;
    if (it.category_id === selectedCat) return true;
    const kids = childIdsByParent[selectedCat] || [];
    return kids.includes(it.category_id);
  };

  const searchFilter = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return () => true;
    return (it) => (it.product_name || "").toLowerCase().includes(q);
  }, [search]);

  const combinedFilter = (it) => catFilter(it) && searchFilter(it);

  const visibleFilteredTotal = useMemo(() => {
    const keys = selectedFresh === "all" ? ["red", "yellow", "green", "expired"] : [selectedFresh];
    return keys.reduce((acc, k) => acc + (totals[k] || []).filter(combinedFilter).length, 0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, selectedCat, selectedFresh, search]);

  const openEdit = (item) => {
    setEditItem({
      ...item,
      quantity: item.quantity || 1,
      category_id: item.category_id || null,
    });
  };

  const closeEdit = () => {
    editOpenRef.current = false; // unblock load() immediately
    setEditItem(null);
  };

  const thumbOf = (it) => (it?.thumb ? `data:image/jpeg;base64,${it.thumb}` : null);

  const toggleFresh = (key) => {
    setSelectedFresh((cur) => (cur === key ? "all" : key));
  };

  return (
    <div className="min-h-screen bg-white pb-32" data-testid="dashboard-page">
      <header className="pt-safe pt-10 px-6">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <PwaIcon size={32} />
            <span className="font-heading text-base font-semibold">Niper</span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <PantrySwitcher />
            <InstallButton />
            {user?.picture && (
              <img src={user.picture} alt="me" className="w-9 h-9 rounded-full border border-zinc-200" />
            )}
          </div>
        </div>

        <h1 className="mt-8 font-heading text-4xl font-medium tracking-tight leading-tight" data-testid="dashboard-title">
          {data.count} {data.count === 1 ? t("common.product") : t("common.products")}<br/>
          <span className="text-zinc-400">{t("dashboard.title.in_pantry")}</span>
        </h1>

        {/* Search bar */}
        <div className="mt-5 relative" data-testid="search-bar">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("dashboard.search.placeholder")}
            className="w-full h-11 pl-11 pr-10 rounded-xl bg-zinc-50 border border-zinc-200 text-sm outline-none focus:border-zinc-400"
            data-testid="search-input"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-zinc-200 flex items-center justify-center"
              aria-label="clear"
              data-testid="search-clear"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        <div className="mt-4 grid grid-cols-4 gap-2">
          <FreshStat active={selectedFresh === "red"} onClick={() => toggleFresh("red")} color="bg-fresh-red-soft" activeBg="bg-fresh-red" text="text-fresh-red-text" activeText="text-white" n={totals.red.length} label={t("dashboard.stats.critical")} testId="stat-red" />
          <FreshStat active={selectedFresh === "yellow"} onClick={() => toggleFresh("yellow")} color="bg-fresh-yellow-soft" activeBg="bg-fresh-yellow" text="text-fresh-yellow-text" activeText="text-black" n={totals.yellow.length} label={t("dashboard.stats.soon")} testId="stat-yellow" />
          <FreshStat active={selectedFresh === "green"} onClick={() => toggleFresh("green")} color="bg-fresh-green-soft" activeBg="bg-fresh-green" text="text-fresh-green-text" activeText="text-black" n={totals.green.length} label={t("dashboard.stats.fresh")} testId="stat-green" />
          <FreshStat active={selectedFresh === "expired"} onClick={() => toggleFresh("expired")} color="bg-zinc-200" activeBg="bg-zinc-900" text="text-zinc-700" activeText="text-white" n={totals.expired.length} label={t("dashboard.stats.expired")} testId="stat-expired" />
        </div>

        {selectedFresh !== "all" && (
          <div className="mt-3 flex items-center justify-between" data-testid="fresh-filter-active">
            <div className="text-[11px] tracking-[0.2em] uppercase font-bold text-zinc-500">
              {t("dashboard.fresh_filter.active")} <span className="text-zinc-900">{labelForGroup(selectedFresh)}</span>
            </div>
            <button
              onClick={() => setSelectedFresh("all")}
              className="h-7 px-3 rounded-full bg-zinc-100 text-zinc-700 text-[11px] font-semibold flex items-center gap-1"
              data-testid="clear-fresh-filter"
            >
              <X className="w-3 h-3" /> {t("dashboard.fresh_filter.clear")}
            </button>
          </div>
        )}

        {/* Category filter chips (nested) */}
        {categories.length > 0 && (
          <div className="mt-5 -mx-6 px-6 overflow-x-auto no-scrollbar" data-testid="category-chips">
            <div className="flex gap-2 pb-1 items-center">
              <CategoryChip
                active={selectedCat === "all"}
                onClick={() => setSelectedCat("all")}
                label={t("dashboard.cat.all")}
                count={totals.red.length + totals.yellow.length + totals.green.length + totals.expired.length}
                testId="chip-all"
              />
              {categories.filter((c) => !c.parent_id).map((parent) => {
                const parentIds = [parent.category_id, ...(childIdsByParent[parent.category_id] || [])];
                const n = ["red", "yellow", "green", "expired"].reduce(
                  (acc, k) => acc + (totals[k] || []).filter((i) => parentIds.includes(i.category_id)).length,
                  0
                );
                const kids = categories.filter((c) => c.parent_id === parent.category_id);
                const isOpen = !!expandedParents[parent.category_id];
                return (
                  <React.Fragment key={parent.category_id}>
                    <div className="relative">
                      <CategoryChip
                        active={selectedCat === parent.category_id}
                        onClick={() => setSelectedCat(parent.category_id)}
                        label={parent.name}
                        emoji={parent.emoji}
                        count={n}
                        testId={`chip-${parent.category_id}`}
                      />
                      {kids.length > 0 && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setExpandedParents((m) => ({ ...m, [parent.category_id]: !isOpen }));
                          }}
                          className={`absolute -right-1 -top-1 w-5 h-5 rounded-full flex items-center justify-center shadow-sm border ${
                            isOpen ? "bg-zinc-950 text-white border-zinc-950" : "bg-white text-zinc-700 border-zinc-200"
                          }`}
                          data-testid={`chip-expand-${parent.category_id}`}
                          aria-label="Mostra sottocategorie"
                        >
                          {isOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                        </button>
                      )}
                    </div>
                    {isOpen && kids.map((k) => {
                      const kn = ["red", "yellow", "green", "expired"].reduce(
                        (acc, key) => acc + (totals[key] || []).filter((i) => i.category_id === k.category_id).length,
                        0
                      );
                      return (
                        <CategoryChip
                          key={k.category_id}
                          active={selectedCat === k.category_id}
                          onClick={() => setSelectedCat(k.category_id)}
                          label={k.name}
                          emoji={k.emoji}
                          count={kn}
                          testId={`chip-${k.category_id}`}
                          subtle
                        />
                      );
                    })}
                  </React.Fragment>
                );
              })}
              <CategoryChip
                active={selectedCat === "none"}
                onClick={() => setSelectedCat("none")}
                label={t("dashboard.cat.none")}
                count={["red", "yellow", "green", "expired"]
                  .reduce((acc, k) => acc + (totals[k] || []).filter((i) => !i.category_id).length, 0)}
                testId="chip-none"
              />
              <button
                onClick={() => setCatManagerOpen(true)}
                className="shrink-0 h-9 px-3 rounded-full border border-dashed border-zinc-300 text-zinc-500 text-xs font-semibold flex items-center gap-1 active:scale-95"
                data-testid="chip-manage"
              >
                <Settings2 className="w-3.5 h-3.5" /> {t("dashboard.cat.manage")}
              </button>
            </div>
          </div>
        )}

        {pendingItems.length > 0 && (
          <div className="mt-5 rounded-2xl bg-zinc-950 text-white px-4 py-3 flex items-center gap-3" data-testid="pending-banner">
            <div className="w-2 h-2 rounded-full bg-fresh-green animate-pulse" />
            <div className="flex-1">
              <div className="text-[11px] tracking-widest uppercase font-bold text-fresh-green">{t("dashboard.banner.pending.label")}</div>
              <div className="text-sm font-medium">{t("dashboard.banner.pending.desc", { n: pendingItems.length })}</div>
            </div>
            <button
              onClick={confirmAll}
              className="px-4 py-2 rounded-full bg-fresh-green text-black text-xs font-bold active:scale-95"
              data-testid="confirm-all-btn"
            >
              {t("dashboard.banner.confirm_all")}
            </button>
          </div>
        )}

        {scanningItems.length > 0 && (
          <div className="mt-3 rounded-2xl bg-fresh-green-soft px-4 py-3 flex items-center gap-3" data-testid="scanning-banner">
            <Loader2 className="w-4 h-4 text-fresh-green-text animate-spin" />
            <div>
              <div className="text-[11px] tracking-widest uppercase font-bold text-fresh-green-text">{t("dashboard.banner.scanning.label")}</div>
              <div className="text-sm text-zinc-900 font-medium">{t("dashboard.banner.scanning.desc", { n: scanningItems.length })}</div>
            </div>
          </div>
        )}

        {reviewItems.length > 0 && (
          <div className="mt-3 rounded-2xl bg-fresh-yellow-soft px-4 py-3 flex items-center gap-3" data-testid="needs-review-banner">
            <span className="w-2 h-2 rounded-full bg-fresh-yellow" />
            <div>
              <div className="text-[11px] tracking-widest uppercase font-bold text-fresh-yellow-text">{t("dashboard.banner.review.label")}</div>
              <div className="text-sm text-zinc-900 font-medium">{t("dashboard.banner.review.desc", { n: reviewItems.length })}</div>
            </div>
          </div>
        )}
      </header>

      <section className="mt-10" data-testid="pantry-sections">
        {pendingItems.length > 0 && selectedFresh === "all" && (
          <div className="mb-6" data-testid="group-pending">
            <div className="px-6 mb-2 flex items-center justify-between">
              <div className="text-[11px] tracking-[0.2em] uppercase font-bold text-zinc-900">{t("dashboard.group.pending")}</div>
              <div className="text-[11px] text-zinc-400 font-medium">{pendingItems.length}</div>
            </div>
            {pendingItems.map((it) => (
              <PendingRow
                key={it.item_id}
                item={it}
                t={t}
                formatDate={formatDate}
                onEdit={() => setEditItem({ ...it, _pending: true })}
                onConfirm={() => confirmOne(it)}
                onDelete={() => onDelete(it)}
              />
            ))}
          </div>
        )}

        {scanningItems.length > 0 && selectedFresh === "all" && (
          <div className="mb-6" data-testid="group-scanning">
            <div className="px-6 mb-2 flex items-center justify-between">
              <div className="text-[11px] tracking-[0.2em] uppercase font-bold text-fresh-green-text">{t("dashboard.group.scanning")}</div>
              <div className="text-[11px] text-zinc-400 font-medium">{scanningItems.length}</div>
            </div>
            {scanningItems.map((it) => (
              <ScanningRow key={it.item_id} item={it} t={t} />
            ))}
          </div>
        )}

        {reviewItems.length > 0 && selectedFresh === "all" && (
          <div className="mb-6" data-testid="group-needs_review">
            <div className="px-6 mb-2 flex items-center justify-between">
              <div className="text-[11px] tracking-[0.2em] uppercase font-bold text-fresh-yellow-text">{t("dashboard.group.review")}</div>
              <div className="text-[11px] text-zinc-400 font-medium">{reviewItems.length}</div>
            </div>
            {reviewItems.map((it) => (
              <ReviewRow
                key={it.item_id}
                item={it}
                t={t}
                formatDate={formatDate}
                onFix={() => setEditItem({ ...it, _pending: true, product_name: it.product_name === "Needs review" ? "" : it.product_name || "", quantity: it.quantity || 1 })}
                onDelete={() => onDelete(it)}
              />
            ))}
          </div>
        )}

        {groups
          .filter((g) => selectedFresh === "all" || selectedFresh === g.key)
          .map((g) => {
            const items = (totals[g.key] || []).filter(combinedFilter);
            if (!items.length) return null;
            return (
              <div key={g.key} className="mb-6" data-testid={`group-${g.key}`}>
                <div className="px-6 mb-2 flex items-center justify-between">
                  <div className={`text-[11px] tracking-[0.2em] uppercase font-bold ${g.tint}`}>{g.label}</div>
                  <div className="text-[11px] text-zinc-400 font-medium">{items.length}</div>
                </div>
                <AnimatePresence>
                  {items.map((it) => (
                    <PantryRow
                      key={it.item_id}
                      item={it}
                      category={categories.find((c) => c.category_id === it.category_id)}
                      onOpen={openEdit}
                      onConsume={onConsume}
                      onDelete={onDelete}
                      onDecrement={onDecrement}
                      onIncrement={onIncrement}
                    />
                  ))}
                </AnimatePresence>
              </div>
            );
          })}

        {!loading && data.count === 0 && scanningItems.length === 0 && reviewItems.length === 0 && pendingItems.length === 0 && (
          <EmptyState onManual={openManualCreate} t={t} />
        )}

        {!loading && data.count > 0 && (selectedCat !== "all" || selectedFresh !== "all" || search.trim()) && visibleFilteredTotal === 0 && (
          <div className="mx-6 mt-4 rounded-2xl bg-zinc-50 border border-zinc-100 px-5 py-8 text-center" data-testid="filter-empty">
            <p className="text-sm text-zinc-500">
              {search.trim() ? t("dashboard.search_empty", { q: search.trim() }) : t("dashboard.filter_empty.title")}
            </p>
            <button onClick={openManualCreate} className="mt-3 px-4 h-10 rounded-full bg-zinc-950 text-white text-xs font-bold" data-testid="filter-empty-add">
              {t("dashboard.filter_empty.add")}
            </button>
          </div>
        )}
      </section>

      <Drawer open={!!editItem} onOpenChange={(o) => !o && closeEdit()}>
        <DrawerContent data-testid="dashboard-edit-drawer">
          {editItem && (
            <div className="mx-auto w-full max-w-md text-zinc-950">
              <DrawerHeader className="text-left">
                <DrawerTitle className="font-heading text-2xl font-medium" data-testid="drawer-title">
                  {editItem._new ? t("drawer.title.new") : editItem._pending ? t("drawer.title.pending") : t("drawer.title.edit")}
                </DrawerTitle>
                <DrawerDescription>
                  {editItem._new
                    ? t("drawer.desc.new")
                    : editItem._pending
                    ? t("drawer.desc.pending")
                    : t("drawer.desc.edit")}
                </DrawerDescription>
              </DrawerHeader>
              <div className="px-4 space-y-4 max-h-[60vh] overflow-y-auto">
                {thumbOf(editItem) && (
                  <div className="flex justify-center" data-testid="drawer-thumb-wrap">
                    <img
                      src={thumbOf(editItem)}
                      alt="label"
                      className="w-32 h-32 rounded-2xl object-cover border border-zinc-200 shadow-sm"
                      data-testid="drawer-thumb"
                    />
                  </div>
                )}
                <div>
                  <label className="text-xs font-medium text-zinc-500 uppercase tracking-wider">{t("drawer.field.product")}</label>
                  <Input
                    value={editItem.product_name || ""}
                    onChange={(e) => setEditItem({ ...editItem, product_name: e.target.value })}
                    placeholder={t("drawer.field.product.placeholder")}
                    className="mt-1 h-12 rounded-xl bg-zinc-50 border-zinc-200"
                    data-testid="dashboard-edit-name"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-zinc-500 uppercase tracking-wider">{t("drawer.field.expiry")}</label>
                  <DateField
                    value={editItem.expiry_date || ""}
                    onChange={(iso) => setEditItem({ ...editItem, expiry_date: iso })}
                    testId="dashboard-edit-expiry"
                    className="mt-1"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-zinc-500 uppercase tracking-wider">{t("drawer.field.qty")}</label>
                  <div className="mt-1 flex items-center gap-3">
                    <button
                      onClick={() => setEditItem({ ...editItem, quantity: Math.max(1, (editItem.quantity || 1) - 1) })}
                      className="w-12 h-12 rounded-xl bg-zinc-100 flex items-center justify-center active:scale-95"
                      data-testid="dashboard-edit-qty-minus"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <div className="flex-1 h-12 rounded-xl bg-zinc-50 border border-zinc-200 font-heading text-xl font-semibold flex items-center justify-center">
                      {editItem.quantity || 1}
                    </div>
                    <button
                      onClick={() => setEditItem({ ...editItem, quantity: (editItem.quantity || 1) + 1 })}
                      className="w-12 h-12 rounded-xl bg-zinc-100 flex items-center justify-center active:scale-95"
                      data-testid="dashboard-edit-qty-plus"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-zinc-500 uppercase tracking-wider">{t("drawer.field.cat")}</label>
                  <div className="mt-2 flex flex-wrap gap-2" data-testid="drawer-category-picker">
                    <button
                      type="button"
                      onClick={() => setEditItem({ ...editItem, category_id: null })}
                      className={`h-9 px-3 rounded-full text-xs font-semibold border transition ${
                        !editItem.category_id
                          ? "bg-zinc-950 text-white border-zinc-950"
                          : "bg-white text-zinc-600 border-zinc-200"
                      }`}
                      data-testid="drawer-cat-none"
                    >
                      {t("drawer.cat.none")}
                    </button>
                    {categories.filter((c) => !c.parent_id).map((p) => (
                      <React.Fragment key={p.category_id}>
                        <button
                          type="button"
                          onClick={() => setEditItem({ ...editItem, category_id: p.category_id })}
                          className={`h-9 px-3 rounded-full text-xs font-bold border transition ${
                            editItem.category_id === p.category_id
                              ? "bg-zinc-950 text-white border-zinc-950"
                              : "bg-white text-zinc-900 border-zinc-200"
                          }`}
                          data-testid={`drawer-cat-${p.category_id}`}
                        >
                          {p.emoji ? `${p.emoji} ` : ""}{p.name}
                        </button>
                        {categories.filter((c) => c.parent_id === p.category_id).map((k) => (
                          <button
                            key={k.category_id}
                            type="button"
                            onClick={() => setEditItem({ ...editItem, category_id: k.category_id })}
                            className={`h-9 pl-2 pr-3 rounded-full text-xs font-semibold border transition flex items-center gap-1 ${
                              editItem.category_id === k.category_id
                                ? "bg-zinc-950 text-white border-zinc-950"
                                : "bg-zinc-50 text-zinc-600 border-zinc-200"
                            }`}
                            data-testid={`drawer-cat-${k.category_id}`}
                          >
                            <span className="text-zinc-400">└</span>
                            {k.emoji ? `${k.emoji} ` : ""}{k.name}
                          </button>
                        ))}
                      </React.Fragment>
                    ))}
                    <button
                      type="button"
                      onClick={() => setCatManagerOpen(true)}
                      className="h-9 px-3 rounded-full text-xs font-semibold border border-dashed border-zinc-300 text-zinc-500 flex items-center gap-1"
                      data-testid="drawer-cat-manage"
                    >
                      <Settings2 className="w-3 h-3" /> {t("drawer.cat.manage")}
                    </button>
                  </div>
                </div>
              </div>
              <DrawerFooter className="flex-row gap-3">
                {editItem._new ? (
                  <>
                    <Button
                      onClick={closeEdit}
                      variant="secondary"
                      className="flex-1 h-12 rounded-xl bg-zinc-100 text-zinc-950"
                      data-testid="dashboard-edit-cancel"
                    >
                      {t("drawer.btn.cancel")}
                    </Button>
                    <Button onClick={createManual} className="flex-1 h-12 rounded-xl bg-fresh-green text-black font-bold" data-testid="dashboard-edit-create">
                      {t("drawer.btn.add")}
                    </Button>
                  </>
                ) : editItem._pending ? (
                  <>
                    <Button
                      onClick={() => { onDelete(editItem); closeEdit(); }}
                      variant="secondary"
                      className="flex-1 h-12 rounded-xl bg-zinc-100 text-zinc-950"
                      data-testid="dashboard-edit-discard"
                    >
                      {t("drawer.btn.discard")}
                    </Button>
                    <Button onClick={saveAndConfirm} className="flex-1 h-12 rounded-xl bg-fresh-green text-black font-bold" data-testid="dashboard-edit-confirm">
                      {t("drawer.btn.save_confirm")}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      onClick={closeEdit}
                      variant="secondary"
                      className="h-12 rounded-xl bg-zinc-100 text-zinc-700 px-4"
                      data-testid="dashboard-edit-cancel"
                    >
                      {t("drawer.btn.cancel")}
                    </Button>
                    <Button
                      onClick={() => { onDelete(editItem); closeEdit(); }}
                      variant="secondary"
                      className="h-12 rounded-xl bg-zinc-100 text-zinc-700 px-4"
                      data-testid="dashboard-edit-delete"
                      aria-label={t("common.delete")}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                    <Button onClick={saveEdit} className="flex-1 h-12 rounded-xl bg-zinc-950 text-white" data-testid="dashboard-edit-save">
                      {t("drawer.btn.save")}
                    </Button>
                  </>
                )}
              </DrawerFooter>
            </div>
          )}
        </DrawerContent>
      </Drawer>

      <BottomNav />

      <CategoryManager
        open={catManagerOpen}
        onOpenChange={setCatManagerOpen}
        onChanged={loadCategories}
      />

      <style>{`.no-scrollbar::-webkit-scrollbar{display:none}.no-scrollbar{scrollbar-width:none}`}</style>
    </div>
  );
}

function FreshStat({ active, onClick, color, activeBg, text, activeText, n, label, testId }) {
  const bg = active ? activeBg : color;
  const txt = active ? activeText : text;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-2xl ${bg} px-3 py-3 text-left transition active:scale-95 ${active ? "ring-2 ring-offset-2 ring-zinc-900/30" : ""}`}
      data-testid={testId}
      aria-pressed={active}
    >
      <div className={`font-heading text-2xl font-semibold ${txt}`}>{n}</div>
      <div className={`text-[10px] font-bold uppercase tracking-widest ${txt} opacity-90`}>{label}</div>
    </button>
  );
}

function CategoryChip({ active, onClick, label, emoji, count, testId, subtle }) {
  let cls;
  if (active) cls = "bg-zinc-950 text-white border-zinc-950";
  else if (subtle) cls = "bg-zinc-50 text-zinc-600 border-zinc-200";
  else cls = "bg-white text-zinc-700 border-zinc-200";
  return (
    <button
      onClick={onClick}
      className={`shrink-0 h-9 ${subtle ? "pl-2 pr-3" : "px-4"} rounded-full text-xs font-semibold border transition whitespace-nowrap flex items-center gap-1 ${cls}`}
      data-testid={testId}
    >
      {subtle && <span className="text-zinc-400">└</span>}
      {emoji ? `${emoji} ` : ""}{label}
      {typeof count === "number" && (
        <span className={`ml-1.5 text-[10px] ${active ? "text-white/70" : "text-zinc-400"}`}>{count}</span>
      )}
    </button>
  );
}

function EmptyState({ onManual, t }) {
  return (
    <div className="mt-6 mx-6 rounded-3xl bg-zinc-50 border border-zinc-100 px-6 py-10 text-center" data-testid="empty-state">
      <img
        src="https://images.pexels.com/photos/13428093/pexels-photo-13428093.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
        alt="empty pantry"
        className="w-40 h-40 object-cover rounded-2xl mx-auto"
      />
      <h3 className="mt-6 font-heading text-2xl font-medium">{t("dashboard.empty.title")}</h3>
      <p className="mt-2 text-zinc-500 text-sm">{t("dashboard.empty.desc")}</p>
      <button
        onClick={onManual}
        className="mt-5 px-5 h-11 rounded-full bg-zinc-950 text-white text-sm font-bold inline-flex items-center gap-1.5 active:scale-95"
        data-testid="empty-manual-add"
      >
        <Pencil className="w-4 h-4" /> {t("dashboard.empty.manual")}
      </button>
    </div>
  );
}

function ScanningRow({ item, t }) {
  return (
    <div className="flex items-center gap-3 bg-white pl-5 pr-4 py-5 border-b border-zinc-100 relative" data-testid={`scanning-row-${item.item_id}`}>
      <span className="absolute left-0 top-2 bottom-2 w-1 rounded-r-full bg-fresh-green" />
      <div className="w-10 h-10 rounded-full bg-fresh-green-soft flex items-center justify-center">
        <Loader2 className="w-5 h-5 text-fresh-green-text animate-spin" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-heading text-base font-medium text-zinc-950">{t("row.scanning.title")}</div>
        <div className="text-xs text-zinc-500 mt-0.5">{t("row.scanning.desc")}</div>
      </div>
    </div>
  );
}

function PendingRow({ item, t, formatDate, onEdit, onConfirm, onDelete }) {
  const isStale = typeof item.days_left === "number" && item.days_left < 0;
  const colorStrip = isStale
    ? "bg-fresh-red"
    : item.real_freshness === "red"
    ? "bg-fresh-red"
    : item.real_freshness === "yellow"
    ? "bg-fresh-yellow"
    : "bg-fresh-green";
  const thumbSrc = item.thumb ? `data:image/jpeg;base64,${item.thumb}` : null;
  const dateLabel = formatDate ? formatDate(item.expiry_date) : item.expiry_date;
  return (
    <div className="bg-white pl-5 pr-4 py-4 border-b border-zinc-100 relative" data-testid={`pending-row-${item.item_id}`}>
      <span className={`absolute left-0 top-2 bottom-2 w-1 rounded-r-full ${colorStrip}`} />
      <div className="flex items-start gap-3">
        {thumbSrc ? (
          <img src={thumbSrc} alt="" className="w-14 h-14 rounded-xl object-cover shrink-0" data-testid={`pending-thumb-${item.item_id}`} />
        ) : (
          <div className="w-14 h-14 rounded-xl bg-zinc-100 shrink-0" />
        )}
        <button onClick={onEdit} className="flex-1 min-w-0 text-left" data-testid={`pending-edit-${item.item_id}`}>
          <div className="font-heading text-lg font-medium text-zinc-950 truncate">{item.product_name}</div>
          <div className="text-xs mt-0.5">
            {isStale ? (
              <span className="text-fresh-red-text font-semibold">{t("row.stale_warn", { date: dateLabel })}</span>
            ) : (
              <span className="text-zinc-500">{t("common.qty")} {item.quantity} · {t("row.label.expires_on", { date: dateLabel })} · {item.days_left >= 0 ? t("common.days_left", { n: item.days_left }) : t("common.expired_days", { n: -item.days_left })}</span>
            )}
          </div>
        </button>
      </div>
      <div className="flex items-center gap-2 mt-3">
        <button
          onClick={onEdit}
          className={`flex-1 h-9 rounded-full text-xs font-semibold flex items-center justify-center gap-1 active:scale-95 ${isStale ? "bg-fresh-red text-white" : "bg-zinc-100 text-zinc-800"}`}
          data-testid={`pending-modify-${item.item_id}`}
        >
          <Pencil className="w-3.5 h-3.5" /> {isStale ? t("row.fix_date") : t("row.modify")}
        </button>
        <button
          onClick={onDelete}
          className="w-9 h-9 rounded-full bg-zinc-100 text-zinc-600 flex items-center justify-center active:scale-95"
          data-testid={`pending-delete-${item.item_id}`}
          aria-label={t("common.delete")}
        >
          <Trash2 className="w-4 h-4" />
        </button>
        <button
          onClick={onConfirm}
          className={`flex-[1.2] h-9 rounded-full text-xs font-bold flex items-center justify-center gap-1 active:scale-95 ${isStale ? "bg-zinc-200 text-zinc-500" : "bg-fresh-green text-black"}`}
          data-testid={`pending-confirm-${item.item_id}`}
          disabled={false}
        >
          <Check className="w-4 h-4" strokeWidth={3} /> {t("row.insert")}
        </button>
      </div>
    </div>
  );
}


function ReviewRow({ item, t, formatDate, onFix, onDelete }) {
  const thumbSrc = item.thumb ? `data:image/jpeg;base64,${item.thumb}` : null;
  const dateLabel = formatDate ? formatDate(item.expiry_date) : item.expiry_date;
  return (
    <div className="flex items-center justify-between bg-white pl-5 pr-4 py-5 border-b border-zinc-100 relative" data-testid={`review-row-${item.item_id}`}>
      <span className="absolute left-0 top-2 bottom-2 w-1 rounded-r-full bg-fresh-yellow" />
      {thumbSrc ? (
        <img src={thumbSrc} alt="" className="w-12 h-12 rounded-xl object-cover shrink-0 mr-3" data-testid={`review-thumb-${item.item_id}`} />
      ) : (
        <div className="w-12 h-12 rounded-xl bg-zinc-100 shrink-0 mr-3" />
      )}
      <button onClick={onFix} className="flex-1 min-w-0 pr-3 text-left" data-testid={`review-fix-${item.item_id}`}>
        <div className="font-heading text-lg font-medium text-zinc-950 truncate">
          {item.product_name && item.product_name !== "Needs review" ? item.product_name : t("row.review.title")}
        </div>
        <div className="text-xs text-zinc-500 mt-0.5">
          {item.expiry_date ? `${t("row.label.expires_on", { date: dateLabel })} · ` : ""}{t("row.review.title")}
        </div>
      </button>
      <div className="flex items-center gap-2">
        <button
          onClick={onFix}
          className="px-3 py-2 rounded-full bg-fresh-yellow text-black text-xs font-semibold active:scale-95"
          data-testid={`review-edit-${item.item_id}`}
        >
          {t("row.review.fix")}
        </button>
        <button
          onClick={onDelete}
          className="px-3 py-2 rounded-full bg-zinc-100 text-zinc-700 text-xs font-semibold active:scale-95"
          data-testid={`review-discard-${item.item_id}`}
        >
          {t("row.review.discard")}
        </button>
      </div>
    </div>
  );
}
